/**
 * src/api/client.ts
 * ------------------
 * Axios HTTP client singleton for the Helix Customer backend.
 *
 * Fully implemented this will:
 *   - Create an Axios instance with baseURL = VITE_API_BASE_URL
 *   - Attach the JWT access token from the auth store to every
 *     request via a request interceptor (Authorization: Bearer <token>)
 *   - Handle 401 responses by attempting a silent token refresh and
 *     retrying the original request once; on second failure, sign out.
 *   - Translate Axios errors into typed AppError instances so all
 *     callers receive a consistent error shape.
 *   - Stamp every request with the single organization this deployment
 *     serves (X-Helix-Org-Id) so the backend never has to guess.
 *
 * Key dependencies:
 *   axios, src/stores/auth.ts (for token access & refresh),
 *   src/core/errors.ts
 */
import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { getCurrentOrgId } from '@/lib/org'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) ?? '/api/v1'

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 20_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor — inject auth token
apiClient.interceptors.request.use((config) => {
  // Dynamically import to avoid circular dependency at module load time
  const token = getStoredToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.headers['X-Helix-Org-Id'] = getCurrentOrgId()
  return config
})

// Response interceptor — handle 401 with token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // TODO: call refreshToken() from auth store, retry request
    }
    return Promise.reject(error)
  },
)

/** Retrieve token from localStorage (set by auth store). */
function getStoredToken(): string | null {
  return useAuthStore.getState().accessToken
}

export default apiClient
